'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

const STORAGE_KEY = 'hideServiceAreaPopupUntil';
const HIDE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

const SEOUL_DISTRICTS = [
  '강남구', '강동구', '강북구', '강서구', '광진구',
  '구로구', '금천구', '노원구', '도봉구', '동대문구',
  '마포구', '서대문구', '성동구', '성북구', '송파구',
  '양천구', '영등포구', '용산구', '은평구', '종로구',
  '중구', '중랑구',
];

interface ServiceAreaEntry {
  city: string;
  district: string;
}

export default function ServiceAreaPopup() {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [district, setDistrict] = useState('');
  const [opinion, setOpinion] = useState('');
  const [replyPhone, setReplyPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [serviceAreas, setServiceAreas] = useState<ServiceAreaEntry[]>([]);

  useEffect(() => {
    const hiddenUntilRaw = localStorage.getItem(STORAGE_KEY);
    const hiddenUntil = hiddenUntilRaw ? Number(hiddenUntilRaw) : 0;
    if (!hiddenUntil || Date.now() >= hiddenUntil) {
      setOpen(true);
    }
    fetch('/api/service-areas')
      .then((res) => res.json())
      .then((data) => setServiceAreas(data))
      .catch(() => {});
  }, []);

  const handleClose = () => setOpen(false);

  const handleDontShowAgain = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + HIDE_DURATION_MS));
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!district) return;
    setError('');
    if (replyPhone.trim()) {
      const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
      if (!phoneRegex.test(replyPhone.replace(/-/g, ''))) {
        setError('연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/service-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ district, opinion, replyPhone: replyPhone.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || '요청 전송에 실패했습니다.');
        return;
      }
      setSubmitted(true);
      setTimeout(() => {
        setShowForm(false);
        setSubmitted(false);
        setDistrict('');
        setOpinion('');
        setReplyPhone('');
      }, 2000);
    } catch {
      setError('요청 전송에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4">
      <div className="relative w-full max-w-[420px] rounded-2xl overflow-hidden shadow-2xl">
        {/* Background image at 70% opacity */}
        <div className="absolute inset-0">
          <Image
            src="/images/service_area.png"
            alt="서비스 지역"
            fill
            className="object-cover opacity-70"
          />
        </div>
        {/* White overlay for readability */}
        <div className="absolute inset-0 bg-white/70" />

        {/* Content */}
        <div className="relative z-10 px-6 py-8">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-5">
            서비스 지역 안내
          </h2>

          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p className="text-center">
              현재는 서울시{' '}
              <span className="font-semibold text-[#12375C]">
                {serviceAreas.length > 0
                  ? serviceAreas.map((a) => a.district).join(', ')
                  : '일부 지역'}
              </span>{' '}
              {serviceAreas.length > 0 ? `${serviceAreas.length}개구에서` : ''} 서비스 중에 있습니다.
            </p>
            <p className="text-center text-gray-500">
              속히 서비스 지역을 확장하도록 노력하겠습니다.
            </p>
            <p className="text-center">
              자신의 동네에 서비스를 원하시면,<br />
              아래 <span className="font-semibold">서비스 요청</span> 버튼을 눌러<br />
              소중한 의견 부탁드립니다.
            </p>
          </div>

          {/* Service request form */}
          {showForm ? (
            <div className="mt-6 bg-white/80 rounded-xl p-4 space-y-3 border border-gray-200">
              {submitted ? (
                <p className="text-center text-green-600 font-semibold py-4">
                  소중한 의견 감사합니다!
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      서울시 구 선택
                    </label>
                    <select
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#12375C]/30"
                    >
                      <option value="">-- 구를 선택하세요 --</option>
                      {SEOUL_DISTRICTS.filter(
                        (d) => !serviceAreas.some((a) => a.district === d)
                      ).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      서비스 의견 (선택)
                    </label>
                    <textarea
                      value={opinion}
                      onChange={(e) => setOpinion(e.target.value)}
                      placeholder="원하시는 서비스나 의견을 남겨주세요"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#12375C]/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      답변 받을 연락처 (선택)
                    </label>
                    <input
                      type="tel"
                      value={replyPhone}
                      onChange={(e) => setReplyPhone(e.target.value)}
                      placeholder="010-1234-5678"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#12375C]/30"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      관리자의 답변을 원하실 경우 입력해주세요.
                    </p>
                  </div>
                  {error && (
                    <p className="text-xs text-red-500">{error}</p>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={!district || submitting}
                    className="w-full py-2.5 bg-[#E8723A] text-white rounded-lg text-sm font-semibold hover:bg-[#D4622E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? '보내는 중...' : '보내기'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-2.5 bg-[#12375C] text-white rounded-lg text-sm font-semibold hover:bg-[#0E2D4D] transition-colors"
              >
                우리 동네도 해주세요
              </button>
            </div>
          )}

          {/* Bottom buttons */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={handleDontShowAgain}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              일주일간 보지 않기
            </button>
            <button
              onClick={handleClose}
              className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
            >
              창닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
